import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bot, Send, RotateCcw, CheckCircle, ChevronDown, Lock, ShieldOff, AlertTriangle, FileText, Loader2, Copy, Check } from 'lucide-react'
import * as Dialog from '@radix-ui/react-dialog'

import { api } from '@/lib/api'
import { useWorkspaceStore } from '@/stores/workspace.store'
import { useUpgradeStore } from '@/stores/upgrade.store'
import type { ApiResponse } from '@/types/api'
import type {
  CounselorSession,
  CounselorMessage,
  CounselorBlockStatus,
  CounselorBudgetInfo,
  CounselorSessionData,
  Application,
  Requirement,
} from '@/types/models'
import { filterModelsByTier, DEFAULT_MODEL_BY_TIER } from '@/lib/counselorModels'

const COUNSELOR_FREE_ACCESS = import.meta.env.VITE_COUNSELOR_FREE_ACCESS === 'true'
const SUPPORT_EMAIL = 'support@gradnomad.com'

// ---------------------------------------------------------------------------
// GenerateEssayDialog — scoped to the user's own applications
// ---------------------------------------------------------------------------

function GenerateEssayDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const queryClient = useQueryClient()
  const workspaceId = useWorkspaceStore((s) => s.currentWorkspaceId)

  const [selectedApp, setSelectedApp] = useState<Application | null>(null)
  const [selectedReq, setSelectedReq] = useState<Requirement | null>(null)
  const [essayPrompt, setEssayPrompt] = useState('')
  const [draft, setDraft] = useState('')
  const [wordCount, setWordCount] = useState(0)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [linked, setLinked] = useState(false)

  useEffect(() => {
    if (!open) {
      setSelectedApp(null)
      setSelectedReq(null)
      setEssayPrompt('')
      setDraft('')
      setWordCount(0)
      setSaveSuccess(false)
      setLinked(false)
    }
  }, [open])

  // Pre-fill prompt from requirement description when one is selected
  useEffect(() => {
    setEssayPrompt(selectedReq?.description ?? '')
  }, [selectedReq])

  const { data: appsData, isLoading: appsLoading } = useQuery<{ success: boolean; data: Application[] }>({
    queryKey: ['applications-for-generate', workspaceId],
    queryFn: () => api.get('/v1/applications'),
    enabled: open && !!workspaceId,
    staleTime: 5 * 60_000,
  })
  const applications = appsData?.data ?? []

  const { data: reqsData, isLoading: reqsLoading } = useQuery<{ success: boolean; data: Requirement[] }>({
    queryKey: ['essay-requirements', selectedApp?.id],
    queryFn: () => api.get(`/v1/requirements?applicationId=${selectedApp!.id}`),
    enabled: !!selectedApp?.id,
  })
  const essayRequirements = (reqsData?.data ?? []).filter((r) => r.type === 'ESSAY')

  const wordLimit = (selectedReq?.metadata?.wordLimit as number | null) ?? null

  const generateMutation = useMutation({
    mutationFn: () =>
      api.post<ApiResponse<{ content: string; wordCount: number }>>('/v1/counselor/essay/generate', {
        universityName: selectedApp!.universityName,
        programName: selectedApp!.programName,
        essayName: selectedReq!.title,
        essayPrompt,
        wordLimit,
      }),
    onSuccess: (data) => {
      setDraft(data.data?.content ?? '')
      setWordCount(data.data?.wordCount ?? 0)
    },
  })

  const saveMutation = useMutation({
    mutationFn: () =>
      api.post<ApiResponse<{ documentId: string; requirementLinked: boolean }>>('/v1/counselor/essay/save', {
        universityName: selectedApp!.universityName,
        programName: selectedApp!.programName,
        essayName: selectedReq!.title,
        content: draft,
      }),
    onSuccess: (data) => {
      setSaveSuccess(true)
      setLinked(data.data?.requirementLinked ?? false)
      queryClient.invalidateQueries({ queryKey: ['documents', workspaceId] })
      queryClient.invalidateQueries({ queryKey: ['applications', workspaceId] })
      queryClient.invalidateQueries({ queryKey: ['requirements'] })
    },
  })

  const showPreview = !!draft || generateMutation.isPending

  const handleDraftChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDraft(e.target.value)
    setWordCount(e.target.value.trim().split(/\s+/).filter(Boolean).length)
    setSaveSuccess(false)
  }, [])

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-xl border bg-background shadow-xl focus:outline-none flex flex-col max-h-[90vh]">
          <div className="flex items-center justify-between border-b px-6 py-4">
            <Dialog.Title className="text-base font-semibold text-foreground">Generate Essay Draft</Dialog.Title>
            <Dialog.Close className="text-muted-foreground hover:text-foreground text-sm">✕</Dialog.Close>
          </div>

          <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

            {/* Step 1: Application picker */}
            {!selectedApp && (
              <div className="space-y-3">
                <label className="text-sm font-medium text-foreground">Select an application</label>
                {appsLoading && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                  </div>
                )}
                {!appsLoading && applications.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No applications found. Add programs to your Kanban board first.
                  </p>
                )}
                {applications.length > 0 && (
                  <ul className="rounded-lg border divide-y max-h-64 overflow-y-auto">
                    {applications.map((app) => (
                      <li key={app.id}>
                        <button
                          type="button"
                          onClick={() => setSelectedApp(app)}
                          className="w-full text-left px-4 py-3 text-sm hover:bg-accent"
                        >
                          <span className="font-medium">{app.universityName}</span>
                          <span className="text-muted-foreground"> — {app.programName}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Step 2: Essay requirement picker */}
            {selectedApp && !selectedReq && !showPreview && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedApp(null)}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    ← Back
                  </button>
                  <span className="text-sm font-medium text-foreground">
                    {selectedApp.universityName} — {selectedApp.programName}
                  </span>
                </div>
                {reqsLoading && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" /> Loading essays…
                  </div>
                )}
                {!reqsLoading && essayRequirements.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No essay requirements found for this application.
                  </p>
                )}
                {essayRequirements.length > 0 && (
                  <ul className="space-y-2">
                    {essayRequirements.map((req) => {
                      const reqWordLimit = (req.metadata?.wordLimit as number | null) ?? null
                      return (
                        <li key={req.id}>
                          <button
                            type="button"
                            onClick={() => setSelectedReq(req)}
                            className="w-full text-left rounded-lg border px-4 py-3 text-sm hover:bg-accent transition-colors"
                          >
                            <p className="font-medium">{req.title}</p>
                            {req.description && (
                              <p className="text-muted-foreground mt-0.5 line-clamp-2">{req.description}</p>
                            )}
                            {reqWordLimit && (
                              <p className="text-xs text-muted-foreground mt-1">{reqWordLimit} words</p>
                            )}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            )}

            {/* Step 2b: Confirm / edit prompt before generating */}
            {selectedReq && !showPreview && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedReq(null)}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    ← Back
                  </button>
                  <span className="text-sm font-medium text-foreground">
                    {selectedApp?.universityName} — {selectedReq.title}
                  </span>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">Essay prompt</label>
                  <textarea
                    value={essayPrompt}
                    onChange={(e) => setEssayPrompt(e.target.value)}
                    rows={4}
                    placeholder="Paste or edit the essay prompt…"
                    className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                  />
                </div>
                {wordLimit && (
                  <p className="text-xs text-muted-foreground">Word limit: {wordLimit}</p>
                )}
              </div>
            )}

            {/* Step 3: Preview + edit draft */}
            {showPreview && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => { setDraft(''); setWordCount(0); setSaveSuccess(false) }}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      ← Back
                    </button>
                    <span className="text-sm font-medium text-foreground">
                      {selectedApp?.universityName} — {selectedReq?.title}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground">{wordCount} words</span>
                </div>
                {generateMutation.isPending ? (
                  <div className="flex items-center justify-center h-48 gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Generating draft…
                  </div>
                ) : (
                  <textarea
                    value={draft}
                    onChange={handleDraftChange}
                    rows={16}
                    className="w-full rounded-lg border bg-background px-4 py-3 text-sm leading-relaxed focus:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                  />
                )}
                {generateMutation.isError && (
                  <p className="text-xs text-destructive">
                    {generateMutation.error instanceof Error ? generateMutation.error.message : 'Generation failed.'}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t px-6 py-4 flex items-center justify-between gap-3">
            <div>
              {saveMutation.isError && (
                <p className="text-xs text-destructive">Save failed. Please try again.</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {selectedReq && !showPreview && (
                <button
                  type="button"
                  onClick={() => generateMutation.mutate()}
                  disabled={generateMutation.isPending || !essayPrompt.trim()}
                  className="rounded-md bg-brand-blue px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
                >
                  Generate Draft
                </button>
              )}
              {showPreview && !generateMutation.isPending && (
                <>
                  <button
                    type="button"
                    onClick={() => generateMutation.mutate()}
                    disabled={generateMutation.isPending}
                    className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-accent disabled:opacity-60"
                  >
                    Regenerate
                  </button>
                  {saveSuccess ? (
                    <span className="flex items-center gap-1.5 text-sm text-brand-green font-medium">
                      <CheckCircle className="h-4 w-4" />
                      {linked ? 'Saved and linked to application' : 'Saved to Documents'}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => saveMutation.mutate()}
                      disabled={saveMutation.isPending || !draft}
                      className="rounded-md bg-brand-blue px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
                    >
                      {saveMutation.isPending ? 'Saving…' : 'Save to Documents'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EssayDraftPayload {
  universityName: string
  programName: string
  essayName: string
  wordLimit: number | null
  content: string
  messageId: string
}

interface SendMessageResponse {
  message: CounselorMessage
  sessionPhase: string
  profileReady: boolean
  profileParseError?: boolean
  budgetInfo?: CounselorBudgetInfo | null
  blockStatus?: CounselorBlockStatus
  essayDraft?: EssayDraftPayload
}

// ---------------------------------------------------------------------------
// EssayDraftCard
// ---------------------------------------------------------------------------

function EssayDraftCard({ draft, workspaceId }: { draft: EssayDraftPayload; workspaceId: string | null }) {
  const queryClient = useQueryClient()
  const [text, setText] = useState(draft.content)
  const [copied, setCopied] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [linked, setLinked] = useState(false)
  const wordCount = text.trim().split(/\s+/).filter(Boolean).length

  const saveMutation = useMutation({
    mutationFn: () =>
      api.post<{ success: boolean; data: { requirementLinked: boolean } }>('/v1/counselor/essay/save', {
        universityName: draft.universityName,
        programName: draft.programName,
        essayName: draft.essayName,
        content: text,
      }),
    onSuccess: (data) => {
      setSaveSuccess(true)
      setLinked(data.data?.requirementLinked ?? false)
      queryClient.invalidateQueries({ queryKey: ['documents', workspaceId] })
      queryClient.invalidateQueries({ queryKey: ['requirements'] })
    },
  })

  function handleCopy() {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="mt-2 rounded-xl border border-brand-blue/20 bg-brand-blue/5 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-brand-blue/20 bg-brand-blue/10">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-brand-blue truncate">
            {draft.universityName} — {draft.essayName}
          </p>
          <p className="text-[10px] text-muted-foreground">
            {wordCount} words{draft.wordLimit ? ` / ${draft.wordLimit} limit` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-3">
          <button
            type="button"
            onClick={handleCopy}
            className="flex items-center gap-1 rounded px-2 py-1 text-[11px] text-muted-foreground hover:bg-background hover:text-foreground transition-colors"
          >
            {copied ? <Check className="h-3 w-3 text-brand-green" /> : <Copy className="h-3 w-3" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
          {saveSuccess ? (
            <span className="flex items-center gap-1 text-[11px] text-brand-green font-medium">
              <CheckCircle className="h-3 w-3" />
              {linked ? 'Saved & linked' : 'Saved'}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
              className="rounded-md bg-brand-blue px-3 py-1 text-[11px] font-semibold text-white hover:opacity-90 disabled:opacity-60"
            >
              {saveMutation.isPending ? 'Saving…' : 'Save to Documents'}
            </button>
          )}
        </div>
      </div>
      <textarea
        value={text}
        onChange={(e) => { setText(e.target.value); setSaveSuccess(false) }}
        rows={12}
        className="w-full bg-transparent px-4 py-3 text-sm leading-relaxed text-foreground focus:outline-none resize-none"
      />
      {saveMutation.isError && (
        <p className="px-4 pb-2 text-xs text-destructive">
          {saveMutation.error instanceof Error ? saveMutation.error.message : 'Save failed.'}
        </p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function formatRenewalDate(isoDate: string): string {
  const d = new Date(isoDate + 'T00:00:00')
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

function CounselorUpsell() {
  const openUpgrade = useUpgradeStore((s) => s.open)
  return (
    <div className="flex h-full flex-col items-center justify-center px-6 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-blue/10 mb-5">
        <Lock className="h-6 w-6 text-brand-blue" />
      </div>
      <h2 className="text-lg font-semibold text-foreground mb-2">AI Counselor is a Pro feature</h2>
      <p className="text-sm text-muted-foreground max-w-sm mb-6">
        Upgrade to Pro or Unlimited to unlock Maya, your personal AI admissions counselor.
      </p>
      <button
        type="button"
        onClick={openUpgrade}
        className="rounded-md bg-brand-blue px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90"
      >
        Upgrade Plan
      </button>
    </div>
  )
}

function CounselorBudgetExhausted({
  tier,
  nextRenewalDate,
}: {
  tier: 'FREE' | 'PRO' | 'MAX'
  nextRenewalDate: string
}) {
  const openUpgrade = useUpgradeStore((s) => s.open)
  const renewalLabel = formatRenewalDate(nextRenewalDate)

  if (tier === 'MAX') {
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted mb-5">
          <Bot className="h-6 w-6 text-muted-foreground" />
        </div>
        <h2 className="text-lg font-semibold text-foreground mb-2">Monthly limit reached</h2>
        <p className="text-sm text-muted-foreground max-w-sm">
          You have exhausted your monthly usage limit. Your access resets on{' '}
          <span className="font-medium text-foreground">{renewalLabel}</span>.
        </p>
      </div>
    )
  }

  const isPro = tier === 'PRO'

  return (
    <div className="flex h-full flex-col items-center justify-center px-6 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-blue/10 mb-5">
        <Lock className="h-6 w-6 text-brand-blue" />
      </div>
      <h2 className="text-lg font-semibold text-foreground mb-2">Monthly limit reached</h2>
      <p className="text-sm text-muted-foreground max-w-sm mb-1">
        {isPro
          ? 'You have used your Pro monthly AI budget. Upgrade to Unlimited to continue, or wait for your renewal.'
          : 'You have used your free monthly AI budget. Upgrade to continue working with Maya.'}
      </p>
      <p className="text-xs text-muted-foreground mb-6">
        Resets on <span className="font-medium text-foreground">{renewalLabel}</span>
      </p>
      <button
        type="button"
        onClick={openUpgrade}
        className="rounded-md bg-brand-blue px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90"
      >
        {isPro ? 'Upgrade to Unlimited' : 'Upgrade Plan'}
      </button>
    </div>
  )
}

function CounselorBlocked({ blockStatus }: { blockStatus: CounselorBlockStatus }) {
  const [timeLeft, setTimeLeft] = useState('')

  useEffect(() => {
    if (blockStatus.isPermanent || !blockStatus.blockedUntil) return

    function update() {
      const diff = new Date(blockStatus.blockedUntil!).getTime() - Date.now()
      if (diff <= 0) {
        setTimeLeft('Access should be restored. Please refresh.')
        return
      }
      const h = Math.floor(diff / 3_600_000)
      const m = Math.floor((diff % 3_600_000) / 60_000)
      const s = Math.floor((diff % 60_000) / 1_000)
      setTimeLeft(`${h}h ${m}m ${s}s`)
    }

    update()
    const id = setInterval(update, 1000)
    return () => clearInterval(id)
  }, [blockStatus.blockedUntil, blockStatus.isPermanent])

  const warningsByCount: Record<number, string> = {
    1: 'First offence — 24-hour suspension.',
    2: 'Second offence — 72-hour suspension.',
    3: 'Third offence — 7-day suspension.',
  }

  return (
    <div className="flex h-full flex-col items-center justify-center px-6 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10 mb-5">
        <ShieldOff className="h-6 w-6 text-destructive" />
      </div>

      {blockStatus.isPermanent ? (
        <>
          <h2 className="text-lg font-semibold text-foreground mb-2">Access permanently disabled</h2>
          <p className="text-sm text-muted-foreground max-w-sm mb-4">
            Your access to AI Counselor has been permanently disabled due to repeated misuse.
          </p>
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=AI%20Counselor%20permanent%20block`}
            className="text-sm font-medium text-brand-blue underline underline-offset-2"
          >
            Contact {SUPPORT_EMAIL} to appeal
          </a>
        </>
      ) : (
        <>
          <h2 className="text-lg font-semibold text-foreground mb-2">AI Counselor temporarily suspended</h2>
          <p className="text-sm text-muted-foreground max-w-sm mb-1">
            {warningsByCount[blockStatus.violationCount] ?? 'Access suspended due to off-topic use.'}
          </p>
          <p className="text-sm text-muted-foreground mb-5">
            Time remaining: <span className="font-medium text-foreground">{timeLeft}</span>
          </p>
          <p className="text-xs text-muted-foreground max-w-xs">
            AI Counselor is for graduate admissions guidance only. Further violations will result
            in longer suspensions and eventual permanent disablement.
          </p>
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// BudgetIndicator
// ---------------------------------------------------------------------------

function BudgetIndicator({ budgetInfo }: { budgetInfo: CounselorBudgetInfo }) {
  const pctUsed = budgetInfo.budgetUsd > 0
    ? Math.min(100, (budgetInfo.spentUsd / budgetInfo.budgetUsd) * 100)
    : 0
  const pctRemaining = 100 - pctUsed
  const isLow = pctRemaining < 20

  return (
    <div className="flex flex-col gap-0.5 min-w-[100px]" title={`$${budgetInfo.spentUsd.toFixed(4)} spent of $${budgetInfo.budgetUsd.toFixed(2)} monthly budget`}>
      <div className="flex items-center justify-between">
        <span className={`text-[10px] font-medium ${isLow ? 'text-destructive' : 'text-muted-foreground'}`}>
          AI Budget
        </span>
        <span className={`text-[10px] ${isLow ? 'text-destructive' : 'text-muted-foreground'}`}>
          {Math.round(pctRemaining)}%
        </span>
      </div>
      <div className="h-1 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${isLow ? 'bg-destructive' : 'bg-brand-blue'}`}
          style={{ width: `${pctRemaining}%` }}
        />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// CounselorPage
// ---------------------------------------------------------------------------

export default function CounselorPage() {
  const workspaceId = useWorkspaceStore((s) => s.currentWorkspaceId)
  const subscriptionTier = useWorkspaceStore((s) => s.currentWorkspace?.subscriptionTier ?? 'FREE')
  const queryClient = useQueryClient()

  if (!COUNSELOR_FREE_ACCESS && subscriptionTier === 'FREE') {
    return <CounselorUpsell />
  }

  const availableModels = filterModelsByTier(subscriptionTier)
  const defaultModel = DEFAULT_MODEL_BY_TIER[subscriptionTier]

  const [input, setInput] = useState('')
  const [selectedModel, setSelectedModel] = useState(defaultModel)
  const [resetDialogOpen, setResetDialogOpen] = useState(false)
  const [profileReady, setProfileReady] = useState(false)
  const [savingProfile, setSavingProfile] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [liveBlockStatus, setLiveBlockStatus] = useState<CounselorBlockStatus | null>(null)
  const [budgetInfo, setBudgetInfo] = useState<CounselorBudgetInfo | null>(null)
  const [profileParseError, setProfileParseError] = useState(false)
  const [essayDialogOpen, setEssayDialogOpen] = useState(false)
  const [essayDrafts, setEssayDrafts] = useState<Record<string, EssayDraftPayload>>({})
  const [focusSchool, setFocusSchool] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const { data: appsData } = useQuery<{ success: boolean; data: Array<{ id: string; universityName: string }> }>({
    queryKey: ['applications-brief', workspaceId],
    queryFn: () => api.get('/v1/applications'),
    enabled: !!workspaceId && profileReady,
    staleTime: 5 * 60_000,
  })

  const schoolOptions = useMemo(() => {
    const seen = new Set<string>()
    const result: string[] = []
    for (const app of appsData?.data ?? []) {
      if (!seen.has(app.universityName)) {
        seen.add(app.universityName)
        result.push(app.universityName)
      }
    }
    return result.sort()
  }, [appsData])

  // Fetch session + block status
  const { data: sessionData } = useQuery<ApiResponse<CounselorSessionData>>({
    queryKey: ['counselor-session', workspaceId],
    queryFn: () => api.get<ApiResponse<CounselorSessionData>>('/v1/counselor/session'),
    enabled: !!workspaceId,
  })

  const session = sessionData?.data?.session
  const blockStatus = liveBlockStatus ?? sessionData?.data?.blockStatus ?? null

  // Fetch messages
  const { data: messagesData } = useQuery<ApiResponse<CounselorMessage[]>>({
    queryKey: ['counselor-messages', workspaceId],
    queryFn: () => api.get<ApiResponse<CounselorMessage[]>>('/v1/counselor/session/messages'),
    enabled: !!workspaceId,
  })

  const messages = messagesData?.data ?? []

  useEffect(() => {
    if (session?.phase === 'ESSAY_WRITING' || session?.phase === 'COMPLETED') {
      setProfileReady(true)
    }
  }, [session?.phase])

  useEffect(() => {
    if (sessionData?.data?.budgetInfo !== undefined) {
      setBudgetInfo(sessionData.data.budgetInfo)
    }
  }, [sessionData?.data?.budgetInfo])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const sendMutation = useMutation({
    mutationFn: (content: string) =>
      api.post<ApiResponse<SendMessageResponse>>('/v1/counselor/session/messages', {
        content,
        modelId: selectedModel,
        ...(focusSchool ? { focusSchool } : {}),
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['counselor-messages', workspaceId] })
      queryClient.invalidateQueries({ queryKey: ['counselor-session', workspaceId] })
      if (data.data?.profileReady) setProfileReady(true)
      if (data.data?.profileParseError) setProfileParseError(true)
      if (data.data?.blockStatus) setLiveBlockStatus(data.data.blockStatus)
      if (data.data?.budgetInfo !== undefined) setBudgetInfo(data.data.budgetInfo)
      if (data.data?.essayDraft) {
        const d = data.data.essayDraft
        setEssayDrafts((prev) => ({ ...prev, [d.messageId]: d }))
      }
    },
  })

  const resetMutation = useMutation({
    mutationFn: () => api.post<ApiResponse<CounselorSession>>('/v1/counselor/session/reset', {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['counselor-messages', workspaceId] })
      queryClient.invalidateQueries({ queryKey: ['counselor-session', workspaceId] })
      setProfileReady(false)
      setSaveSuccess(false)
      setEssayDrafts({})
      setFocusSchool(null)
      setResetDialogOpen(false)
    },
  })

  async function handleSaveProfile() {
    setSavingProfile(true)
    try {
      await api.post('/v1/counselor/session/profile/save', {})
      setSaveSuccess(true)
      queryClient.invalidateQueries({ queryKey: ['documents', workspaceId] })
    } catch {
      // Silently fail — the data is still in the session
    } finally {
      setSavingProfile(false)
    }
  }

  function handleSend(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = input.trim()
    if (!trimmed || sendMutation.isPending) return
    setInput('')
    sendMutation.mutate(trimmed)
  }

  // Show block screen if blocked (from session load or just triggered by a response)
  if (blockStatus?.isBlocked) {
    return <CounselorBlocked blockStatus={blockStatus} />
  }

  const effectiveBudgetInfo = budgetInfo ?? sessionData?.data?.budgetInfo ?? null
  const isBudgetExhausted = !!(effectiveBudgetInfo && effectiveBudgetInfo.remainingUsd <= 0)

  if (isBudgetExhausted) {
    return (
      <CounselorBudgetExhausted
        tier={subscriptionTier as 'FREE' | 'PRO' | 'MAX'}
        nextRenewalDate={effectiveBudgetInfo!.nextRenewalDate}
      />
    )
  }

  const phaseLabel =
    session?.phase === 'BACKGROUND_GATHERING'
      ? 'Background Gathering'
      : session?.phase === 'ESSAY_WRITING'
        ? 'Essay Writing'
        : session?.phase === 'COMPLETED'
          ? 'Completed'
          : 'Starting...'

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-blue/10">
            <Bot className="h-4 w-4 text-brand-blue" />
          </div>
          <div>
            <h1 className="text-base font-semibold text-foreground">AI Counselor</h1>
            <p className="text-xs text-muted-foreground">Phase: {phaseLabel}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {budgetInfo && (
            <BudgetIndicator budgetInfo={budgetInfo} />
          )}

          <div className="relative">
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="appearance-none rounded-md border bg-background py-1.5 pl-3 pr-8 text-xs text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {availableModels.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.displayName}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          </div>

          <button
            type="button"
            onClick={() => setResetDialogOpen(true)}
            className="flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset
          </button>
        </div>
      </div>

      {/* Profile ready banner */}
      {profileReady && (
        <div className="flex items-center justify-between border-b bg-brand-green/5 px-6 py-3">
          <div className="flex items-center gap-2 text-sm text-brand-green">
            <CheckCircle className="h-4 w-4" />
            <span className="font-medium">Profile gathered!</span>
            <span className="text-muted-foreground">Your background has been captured.</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setEssayDialogOpen(true)}
              className="flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent"
            >
              <FileText className="h-3.5 w-3.5" />
              Generate Essay
            </button>
            {saveSuccess ? (
              <span className="text-xs text-brand-green font-medium">Saved to Documents</span>
            ) : (
              <button
                type="button"
                onClick={handleSaveProfile}
                disabled={savingProfile}
                className="rounded-md bg-brand-green px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60"
              >
                {savingProfile ? 'Saving...' : 'Save Profile'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* School selector — shown in essay writing phase */}
      {session?.phase === 'ESSAY_WRITING' && (
        <div className="flex items-center gap-3 border-b bg-muted/30 px-6 py-2.5">
          <span className="text-xs text-muted-foreground shrink-0">Working on:</span>
          <div className="relative">
            <select
              value={focusSchool ?? ''}
              onChange={(e) => setFocusSchool(e.target.value || null)}
              className="appearance-none rounded-md border bg-background py-1.5 pl-3 pr-8 text-xs text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">All schools</option>
              {schoolOptions.map((school) => (
                <option key={school} value={school}>{school}</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          </div>
          <span className="text-xs text-muted-foreground">
            {focusSchool
              ? 'System prompt scoped to this school — switch anytime'
              : 'Select a school to focus the AI on that school\'s essays'}
          </span>
        </div>
      )}

      {/* Profile parse error banner */}
      {profileParseError && (
        <div className="flex items-center justify-between border-b bg-yellow-500/5 px-6 py-3">
          <div className="flex items-center gap-2 text-sm text-yellow-600 dark:text-yellow-400">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              Maya finished gathering your profile but we had trouble reading it. Send one more
              message and she'll try again.
            </span>
          </div>
          <button
            type="button"
            onClick={() => setProfileParseError(false)}
            className="ml-4 text-xs text-muted-foreground hover:text-foreground shrink-0"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {messages.length === 0 && !sendMutation.isPending && (
          <div className="flex flex-col items-center justify-center h-full text-center py-12">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-blue/10 mb-4">
              <Bot className="h-6 w-6 text-brand-blue" />
            </div>
            <h3 className="text-base font-semibold text-foreground mb-1">Meet Maya</h3>
            <p className="text-sm text-muted-foreground max-w-sm">
              Your AI admissions counselor. She'll guide you through sharing your background so we
              can craft your most compelling story.
            </p>
            <button
              type="button"
              onClick={() => sendMutation.mutate("Hi Maya, I'm ready to get started.")}
              disabled={sendMutation.isPending}
              className="mt-6 rounded-md bg-brand-blue px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
            >
              Start Conversation
            </button>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex flex-col ${msg.role === 'USER' ? 'items-end' : 'items-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-xl px-4 py-3 text-sm leading-relaxed ${
                msg.role === 'USER'
                  ? 'bg-brand-blue text-white'
                  : 'bg-muted text-foreground'
              }`}
            >
              <p className="whitespace-pre-wrap">{msg.content}</p>
              <p className={`mt-1 text-[10px] ${msg.role === 'USER' ? 'text-white/60' : 'text-muted-foreground'}`}>
                {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
            {essayDrafts[msg.id] && (
              <div className="w-full max-w-[80%]">
                <EssayDraftCard draft={essayDrafts[msg.id]} workspaceId={workspaceId} />
              </div>
            )}
          </div>
        ))}

        {sendMutation.isPending && (
          <div className="flex justify-start">
            <div className="rounded-xl bg-muted px-4 py-3">
              <div className="flex gap-1">
                <span className="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:-0.3s]" />
                <span className="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:-0.15s]" />
                <span className="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce" />
              </div>
            </div>
          </div>
        )}

        {sendMutation.isError && (
          <p className="text-center text-xs text-destructive">
            {sendMutation.error instanceof Error
              ? sendMutation.error.message
              : 'Something went wrong. Please try again.'}
          </p>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="border-t px-6 py-4">
        <div className="flex gap-3">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type your message..."
            disabled={sendMutation.isPending}
            className="flex-1 rounded-xl border bg-background px-4 py-2.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={!input.trim() || sendMutation.isPending}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-blue text-white hover:opacity-90 disabled:opacity-40"
            aria-label="Send"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </form>

      {/* Essay generation dialog */}
      <GenerateEssayDialog open={essayDialogOpen} onOpenChange={setEssayDialogOpen} />

      {/* Reset confirmation dialog */}
      <Dialog.Root open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-xl border bg-background p-6 shadow-xl focus:outline-none">
            <Dialog.Title className="text-base font-semibold text-foreground mb-2">
              Reset Conversation?
            </Dialog.Title>
            <Dialog.Description className="text-sm text-muted-foreground mb-6">
              This will permanently delete all messages and the gathered profile. This action cannot
              be undone.
            </Dialog.Description>
            <div className="flex gap-3 justify-end">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-accent"
                >
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="button"
                onClick={() => resetMutation.mutate()}
                disabled={resetMutation.isPending}
                className="rounded-md bg-destructive px-4 py-2 text-sm font-semibold text-destructive-foreground hover:opacity-90 disabled:opacity-60"
              >
                {resetMutation.isPending ? 'Resetting...' : 'Reset'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  )
}
